import { Sidebar } from '../Sidebar';

const downloadBase64Pdf = (base64File, filename) => {
    const byteCharacters = atob(base64File);
    const byteNumbers = Array.from(byteCharacters, char => char.charCodeAt(0));
    const blob = new Blob([new Uint8Array(byteNumbers)], {
        type: 'application/pdf',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

export const initInspirationSidebar = listing => {
    if (listing.inspirationSidebar) {
        return;
    }

    listing.inspirationSidebar = new Sidebar({
        overlayClassNames: ['bg-grey-900/60'],
    });
};

export const fetchInspirationDetail = async (listing, termId) => {
    if (typeof ajax === 'undefined') {
        return null;
    }

    const normalizedTermId = Number(termId);

    if (!Number.isFinite(normalizedTermId) || normalizedTermId <= 0) {
        return null;
    }

    const formData = new FormData();
    formData.append('action', 'mda_inspiration_sidebar');
    formData.append('nonce', ajax.nonce);
    formData.append('term_id', String(normalizedTermId));

    try {
        const response = await fetch(ajax.url, {
            method: 'POST',
            body: formData,
            credentials: 'same-origin',
        });

        if (!response.ok) {
            return null;
        }

        const result = await response.json();

        if (!result?.success || typeof result?.data?.html !== 'string') {
            return null;
        }

        return {
            html: result.data.html.trim(),
        };
    } catch {
        return null;
    }
};

export const applyInspirationSidebarHtml = (listing, html) => {
    if (!listing.inspirationSidebar?.content) {
        return null;
    }

    listing.inspirationSidebar.content.innerHTML = html;

    const root = listing.inspirationSidebar.content.querySelector(
        '[data-inspiration-sidebar]'
    );

    if (root && typeof window.mdaMinicart?.initSidebarEnhancements === 'function') {
        window.mdaMinicart.initSidebarEnhancements(root);
    }

    const galleryEl = root?.querySelector('.js-gallery');

    if (galleryEl && typeof window.mdaInitProductGallery === 'function') {
        window.mdaInitProductGallery(galleryEl);
    }

    return root;
};

export const fetchInspirationShoppingList = async (listing, termId) => {
    if (typeof ajax === 'undefined') {
        return null;
    }

    const normalizedTermId = Number(termId);

    if (!Number.isFinite(normalizedTermId) || normalizedTermId <= 0) {
        return null;
    }

    const formData = new FormData();
    formData.append('action', 'mda_inspiration_shopping_list');
    formData.append('nonce', ajax.nonce);
    formData.append('term_id', String(normalizedTermId));

    try {
        const response = await fetch(ajax.url, {
            method: 'POST',
            body: formData,
            credentials: 'same-origin',
        });

        if (!response.ok) {
            return null;
        }

        const result = await response.json();

        if (!result?.success || typeof result?.data?.html !== 'string') {
            return null;
        }

        return result.data.html.trim();
    } catch {
        return null;
    }
};

export const syncInspirationShoppingList = async (listing, termId) => {
    const normalizedTermId = Number(termId || listing?.activeInspirationTermId || 0);

    if (normalizedTermId <= 0 || !listing?.inspirationSidebar?.content) {
        return;
    }

    const html = await fetchInspirationShoppingList(listing, normalizedTermId);

    if (!html) {
        return;
    }

    const temp = document.createElement('div');
    temp.innerHTML = html;

    const nextList = temp.querySelector('[data-inspiration-shopping-list]');
    const currentRoot = listing.inspirationSidebar.content.querySelector(
        '[data-inspiration-sidebar]'
    );
    const currentList = currentRoot?.querySelector(
        '[data-inspiration-shopping-list]'
    );

    if (nextList && currentList && currentRoot) {
        currentList.replaceWith(nextList);

        if (typeof window.mdaMinicart?.initSidebarEnhancements === 'function') {
            window.mdaMinicart.initSidebarEnhancements(currentRoot);
        }
    }
};

export const refreshInspirationShoppingList = async listing => {
    const termId = Number(listing?.activeInspirationTermId || 0);

    if (
        termId <= 0 ||
        !listing?.inspirationSidebar?.content ||
        !listing.inspirationSidebar.root?.classList.contains('-active')
    ) {
        return;
    }

    await syncInspirationShoppingList(listing, termId);
};

export const addInspirationAllToCart = async (listing, termId) => {
    if (typeof ajax === 'undefined') {
        return;
    }

    const normalizedTermId = Number(termId);

    if (!Number.isFinite(normalizedTermId) || normalizedTermId <= 0) {
        return;
    }

    const button = listing?.inspirationSidebar?.content?.querySelector(
        '[data-inspiration-add-all-to-cart]'
    );

    if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
    }

    const formData = new FormData();
    formData.append('action', 'mda_inspiration_add_all_to_cart');
    formData.append('nonce', ajax.nonce);
    formData.append('term_id', String(normalizedTermId));

    try {
        const response = await fetch(ajax.url, {
            method: 'POST',
            body: formData,
            credentials: 'same-origin',
        });

        if (!response.ok) {
            throw new Error('Request failed');
        }

        const result = await response.json();

        if (!result?.success) {
            throw new Error(
                result?.data?.message ||
                    'Nie udało się dodać produktów do koszyka.'
            );
        }

        if (typeof window.mdaMinicart?.markCartDirty === 'function') {
            window.mdaMinicart.markCartDirty();
        }

        const html =
            typeof result?.data?.html === 'string'
                ? result.data.html.trim()
                : '';

        if (html) {
            const temp = document.createElement('div');
            temp.innerHTML = html;

            const nextList = temp.querySelector('[data-inspiration-shopping-list]');
            const currentRoot = listing.inspirationSidebar.content.querySelector(
                '[data-inspiration-sidebar]'
            );
            const currentList = currentRoot?.querySelector(
                '[data-inspiration-shopping-list]'
            );

            if (nextList && currentList && currentRoot) {
                currentList.replaceWith(nextList);

                if (
                    typeof window.mdaMinicart?.initSidebarEnhancements ===
                    'function'
                ) {
                    window.mdaMinicart.initSidebarEnhancements(currentRoot);
                }
            }
        } else {
            await syncInspirationShoppingList(listing, normalizedTermId);
        }

        const failed = Array.isArray(result?.data?.result?.failed)
            ? result.data.result.failed
            : [];

        if (failed.length > 0) {
            window.alert(
                `Nie udało się dodać: ${failed.join(', ')}`
            );
        }
    } catch (error) {
        console.error(error);
        window.alert(
            error?.message || 'Nie udało się dodać produktów do koszyka.'
        );
    } finally {
        if (button) {
            button.disabled = false;
            button.removeAttribute('aria-busy');
        }
    }
};

export const exportInspirationShoppingListPdf = async (listing, termId) => {
    if (typeof ajax === 'undefined') {
        return;
    }

    const normalizedTermId = Number(termId);

    if (!Number.isFinite(normalizedTermId) || normalizedTermId <= 0) {
        return;
    }

    listing.loader?.show();

    const formData = new FormData();
    formData.append('action', 'mda_inspiration_export_pdf');
    formData.append('nonce', ajax.nonce);
    formData.append('term_id', String(normalizedTermId));

    try {
        const response = await fetch(ajax.url, {
            method: 'POST',
            body: formData,
            credentials: 'same-origin',
        });

        if (!response.ok) {
            throw new Error('Request failed');
        }

        const result = await response.json();

        if (!result?.success || typeof result?.data?.file !== 'string') {
            throw new Error(
                result?.data?.message ||
                    'Nie udało się wygenerować pliku PDF.'
            );
        }

        downloadBase64Pdf(result.data.file, `${Date.now()}.pdf`);
    } catch (error) {
        console.error(error);
        window.alert(
            error?.message || 'Nie udało się wygenerować pliku PDF.'
        );
    } finally {
        listing.loader?.hide();
    }
};

export const openInspirationSidebar = async (listing, termId) => {
    initInspirationSidebar(listing);

    if (!listing.inspirationSidebar?.content) {
        return;
    }

    listing.activeInspirationTermId = termId;

    const detail = await fetchInspirationDetail(listing, termId);
    const html = typeof detail?.html === 'string' ? detail.html.trim() : '';

    if (!html) {
        return;
    }

    applyInspirationSidebarHtml(listing, html);
    await syncInspirationShoppingList(listing, termId);
    listing.inspirationSidebar.open();
};
